import axios from "axios";

import { getCookie } from "./cookie";
import logoutAndRedirect from "./logoutAndRedirect";
import { adoptRefreshedToken, getAccessToken, refreshCookieHeaders, setAccessToken } from "./tokenStore";
import { clearLegacyToken, readLegacyToken } from "./legacyToken";

const URL_API = process.env.URL_API;
const refreshTokenURL = `${URL_API}/identity/auth/refresh/suppliers`;
const loginUrl = `${URL_API}/identity/suppliers/login`;
const logoutURL = `${URL_API}/identity/suppliers/logout`;

// sellsmart-api#4626: one refresh at a time across every tab of this portal.
// Tabs share one cookie jar, and each refresh rotates the cookie, so two tabs
// refreshing at once would race (the API's 30 s grace window covers a lost
// response, not a habit). Browsers without the Web Locks API just run it.
const REFRESH_LOCK = "ss-refresh-suppliers";

const withRefreshLock = async <T>(fn: () => Promise<T>): Promise<T> =>
  typeof navigator !== "undefined" && navigator.locks?.request
    ? navigator.locks.request(REFRESH_LOCK, fn)
    : fn();

/** Thrown when a refreshed token belongs to another user (AC-1.15). */
class OtherUserSessionError extends Error {}

/**
 * How a refresh failure is handled (sellsmart-api#4626 AC-1.10):
 * - `refused` (401 / 403): the refresh cookie is dead for every tab, so the
 *   session is over everywhere and the shared session cookies are cleared.
 * - `other-user`: another tab signed in as someone else, which replaced the
 *   cookie. This tab reloads into that session rather than silently becoming
 *   the other user mid-page; nothing shared is touched.
 * - `unavailable` (429, 5xx, network, timeout): the session is fine; this
 *   request just fails, and nobody is signed out.
 */
const classify = (error: unknown): "refused" | "other-user" | "unavailable" => {
  if (error instanceof OtherUserSessionError) {
    return "other-user";
  }
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403 ? "refused" : "unavailable";
};

/**
 * One refresh against the API. The HttpOnly refresh cookie travels with
 * `withCredentials`; the first refresh after this release also sends the old
 * stored token as the bearer, once, so the API can migrate the session to
 * the cookie (ADR-5). Throws when the session cannot be refreshed.
 */
const performRefresh = async (): Promise<string> =>
  withRefreshLock(async () => {
    const legacy = getAccessToken() ? null : readLegacyToken();
    const response = await axios.post(
      refreshTokenURL,
      { remember: Boolean(getCookie("keepLoggedIn")) },
      {
        withCredentials: true,
        // A hung refresh would hold the cross-tab lock and every tab's render.
        timeout: 15000,
        headers: {
          ...refreshCookieHeaders(),
          ...(legacy ? { Authorization: `Bearer ${legacy}` } : {}),
        },
      },
    );
    const newAccessToken: string = response.data.accessToken;

    if (!adoptRefreshedToken(newAccessToken)) {
      throw new OtherUserSessionError("The refreshed session belongs to another user.");
    }

    clearLegacyToken();
    axios.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
    return newAccessToken;
  });

const reloadIntoCurrentSession = () => {
  setAccessToken(null);
  delete axios.defaults.headers.common["Authorization"];
  window.location.reload();
};

// Singleton pattern for refresh token
let refreshTokenPromise: Promise<string | null> | null = null;

/**
 * Gets a new access token from the HttpOnly refresh cookie. Resolves null
 * when the session is over (the caller has been signed out); rejects with the
 * refresh's own error when the API is unavailable, so a request fails
 * without signing anyone out.
 */
const refreshToken = async (): Promise<string | null> => {
  // If there's already a refresh token request in progress, return that promise
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  // #4160: no session at all means there is nothing to refresh, and the round
  // trip let a concurrent sign-in land first and then be logged straight back
  // out. With #4626 the token is in memory, so "a session" is the
  // `authenticated` flag or a not-yet-migrated stored token.
  //
  // This must stay OUTSIDE the promise below: a throw before the first await
  // runs the IIFE's finally synchronously, BEFORE the assignment, which would
  // leave refreshTokenPromise holding a settled null for the page's lifetime.
  if (!getAccessToken() && !getCookie("authenticated") && !readLegacyToken()) {
    logoutAndRedirect("/sign-in");
    return null;
  }

  // Create new refresh token promise
  refreshTokenPromise = (async () => {
    try {
      return await performRefresh();
    } catch (error) {
      const kind = classify(error);
      if (kind === "refused") {
        logoutAndRedirect("/sign-in");
        return null;
      }
      if (kind === "other-user") {
        reloadIntoCurrentSession();
        return null;
      }
      throw error;
    } finally {
      // Clear the promise after completion (success or failure)
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

export type RestoreResult = "restored" | "none" | "refused" | "unavailable";

/**
 * On page load there is no access token in memory. Restores it from the
 * refresh cookie (or migrates a stored token) when this browser had a
 * session. `refused` means the session is over; `unavailable` means the API
 * could not answer, and the page should load anyway (its requests refresh
 * again).
 */
export const restoreSession = async (): Promise<RestoreResult> => {
  if (getAccessToken()) {
    return "restored";
  }
  if (!getCookie("authenticated") && !readLegacyToken()) {
    return "none";
  }
  try {
    await performRefresh();
    return "restored";
  } catch (error) {
    // A fresh page has no user in memory, so "another user" cannot happen here.
    return classify(error) === "refused" ? "refused" : "unavailable";
  }
};

// Add a request interceptor
axios.interceptors.request.use(
  async (config) => {
    let token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    // Do something with response data
    return response;
  },
  async function (error) {
    // Do something with response error
    const originalRequest = error.config;

    // return error if the error is 5xx for logout API
    if (originalRequest?.url === logoutURL && error.response?.status >= 500) {
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== refreshTokenURL &&
      originalRequest.url !== loginUrl
    ) {
      originalRequest._retry = true;
      let newToken: string | null;
      try {
        newToken = await refreshToken();
      } catch (refreshError) {
        // The API could not refresh right now (429, 5xx, network): fail this
        // request with that error, not the 401, so no caller signs out.
        return Promise.reject(refreshError);
      }
      if (newToken) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return axios(originalRequest);
      }
      // A null token means refreshToken() already signed the tab out (or is
      // reloading it into the current session); nothing more to do here.
    }

    return Promise.reject(error);
  },
);

export default async function request(
  endpoint: string,
  opts: any = {},
  baseURL = process.env.URL_API,
) {
  const token = getAccessToken();

  const { headers, ...options } = opts;
  const config = Object.assign(
    {
      url: endpoint,
      baseURL,
      headers: Object.assign(
        {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        headers,
      ),
      timeout: 30000,
    },
    options,
  );

  console.log("request custom function");

  const { data: result } = await axios.request(config);
  return result;
}
